import {
  View,
  Text,
  TouchableOpacity,
  Alert,
  Image,
  ScrollView,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Modal,
  Pressable,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useState, useCallback } from "react";
import { useFocusEffect } from "expo-router";
import customAxios from "../../../utils/customAxios";
import i18n from "../../../locales/i18n";
import FloatingLabelInput from "../../../components/FloatingLabelInput";
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { Toast } from "react-native-toast-notifications";
import { useAudioPlayer } from "expo-audio";
import CameraScanner from "../../../components/scanner/CameraScanner";
import * as ImagePicker from "expo-image-picker";
import * as ImageManipulator from "expo-image-manipulator";
import { router, useLocalSearchParams } from "expo-router";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import { Picker } from "@react-native-picker/picker";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";
import { appActions } from "../../../store/slices/appSlice";
import AddProductHeaderIcon from "../../../assets/stock/add-product.svg";
import WebAddProductScreen from "../../../components/web/WebAddProductScreen";
import Animated, { FadeInDown, FadeInUp } from "react-native-reanimated";
import { useStock } from "../../../hooks/useStock";
import CustomAlert from "../../../components/ui/CustomAlert";

const AddProduct = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { locale } = useSelector((state: RootState) => state.app);

  const dispatch = useDispatch();
  const { deleteProduct } = useStock();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get route parameters and convert to strings
  const params = useLocalSearchParams();
  const isEditMode = params.mode === "edit";

  // Convert params to strings to fix TypeScript issues
  const paramProductName = Array.isArray(params.productName)
    ? params.productName[0]
    : params.productName;
  const paramPriceSell = Array.isArray(params.priceSell)
    ? params.priceSell[0]
    : params.priceSell;
  const paramPriceBought = Array.isArray(params.priceBought)
    ? params.priceBought[0]
    : params.priceBought;
  const paramBarcode = Array.isArray(params.barcode)
    ? params.barcode[0]
    : params.barcode;
  const paramTypeOfQuantity = Array.isArray(params.typeOfQuantity)
    ? params.typeOfQuantity[0]
    : params.typeOfQuantity;
  const paramStock = Array.isArray(params.stock)
    ? params.stock[0]
    : params.stock;
  const paramPhoto = Array.isArray(params.photo)
    ? params.photo[0]
    : params.photo;

  const audioPlayer = useAudioPlayer(
    require("../../../assets/sounds/scan.mp3")
  );

  function playSound() {
    audioPlayer.play();
  }

  const [scanning, setScanning] = useState(false);
  const [OpenScan, setOpenScan] = useState(false);
  const [initialPrices, setInitialPrices] = useState<Record<string, string>>(
    {}
  );
  const [isImagePickerAlertVisible, setImagePickerAlertVisible] = useState(false);
  const [sellPrice, setSellPrice] = useState(
    isEditMode ? paramPriceSell || "" : ""
  );
  const [BoughtPrice, setBoughtPrice] = useState(
    isEditMode ? paramPriceBought || "" : ""
  );
  const [barcode, setBarcode] = useState(isEditMode ? paramBarcode || "" : "");
  const [name, setName] = useState(isEditMode ? paramProductName || "" : "");
  // default stock types are "kilogram", "liter", "unit"
  const quantitiesTypes = [
    { label: i18n.t("kilogram"), value: "kilogram" },
    { label: i18n.t("liter"), value: "liter" },
    { label: i18n.t("unit"), value: "unit" },
  ];
  const [typeOfQuantity, setTypeOfQuantity] = useState(
    isEditMode ? paramTypeOfQuantity : "unit"
  );
  const [stock, setStock] = useState(isEditMode ? paramStock || "" : "");

  const [image, setImage] = useState(isEditMode ? paramPhoto || null : null);
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch])
  );

  const [buttonSpinner, setButtonSpinner] = useState(false);

  // Check image size function (returns size in KB)
  const checkImageSize = async (uri: string): Promise<number> => {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();
      const sizeInKB = blob.size / 1024; // Convert to KB instead of MB
      return sizeInKB;
    } catch (error) {
      return 0;
    }
  };

  // Smart image compression with guaranteed 600KB result
  const compressImageTo600KB = async (uri: string): Promise<string> => {
    let currentUri = uri;
    let currentSize = await checkImageSize(uri);

    // If already under 600KB, return as is
    if (currentSize <= 600) {
      return uri;
    }

    // Strategy 1: Try different compression levels
    const qualityLevels = [0.8, 0.6, 0.4, 0.3, 0.2, 0.15, 0.1];

    for (const quality of qualityLevels) {
      try {
        const compressedResult = await ImageManipulator.manipulateAsync(
          currentUri,
          [],
          {
            compress: quality,
            format: ImageManipulator.SaveFormat.JPEG,
            base64: false,
          }
        );

        const compressedSize = await checkImageSize(compressedResult.uri);

        if (compressedSize <= 600) {
          return compressedResult.uri;
        }

        currentUri = compressedResult.uri;
        currentSize = compressedSize;
      } catch (error) {
        // silently ignore
      }
    }

    // Strategy 2: If compression alone isn't enough, try resizing + compression
    const sizes = [
      { width: 1200 },
      { width: 1000 },
      { width: 800 },
      { width: 600 },
      { width: 400 },
    ];

    for (const size of sizes) {
      try {
        const resizedResult = await ImageManipulator.manipulateAsync(
          uri, // Start with original for best quality
          [{ resize: size }],
          {
            compress: 0.3,
            format: ImageManipulator.SaveFormat.JPEG,
            base64: false,
          }
        );

        const resizedSize = await checkImageSize(resizedResult.uri);

        if (resizedSize <= 600) {
          return resizedResult.uri;
        }

        // If still too big, try lower compression on this size
        for (const quality of [0.2, 0.1, 0.05]) {
          const finalResult = await ImageManipulator.manipulateAsync(
            resizedResult.uri,
            [],
            {
              compress: quality,
              format: ImageManipulator.SaveFormat.JPEG,
              base64: false,
            }
          );

          const finalSize = await checkImageSize(finalResult.uri);

          if (finalSize <= 600) {
            return finalResult.uri;
          }
        }
      } catch (error) {
        // silently ignore
      }
    }

    // Strategy 3: Last resort - very small size with very low quality
    try {
      const lastResortResult = await ImageManipulator.manipulateAsync(
        uri,
        [{ resize: { width: 300 } }],
        {
          compress: 0.05,
          format: ImageManipulator.SaveFormat.JPEG,
          base64: false,
        }
      );

      const lastResortSize = await checkImageSize(lastResortResult.uri);

      return lastResortResult.uri;
    } catch (error) {
      return uri; // Return original if everything fails
    }
  };

  // Process and validate image size with automatic compression
  const processImageWithCompression = async (
    sourceUri: string,
    isCamera: boolean = false
  ): Promise<string | null> => {
    try {
      let imageUri = sourceUri;
      let imageSize = await checkImageSize(imageUri);

      // If image is already under 600KB, use it as is
      if (imageSize <= 600) {
        return imageUri;
      }

      // If image is too large, automatically compress it using our guaranteed compression function

      Toast.show("Image is being optimized, please wait...", {
        type: "info",
        duration: 2000,
      });

      // Use our guaranteed compression function
      const compressedUri = await compressImageTo600KB(sourceUri);
      const finalSize = await checkImageSize(compressedUri);

      Toast.show(`Image optimized to ${finalSize.toFixed(0)}KB`, {
        type: "success",
        duration: 2000,
      });

      return compressedUri;
    } catch (error) {
      Toast.show("Failed to process image. Please try again.", {
        type: "danger",
      });
      return null;
    }
  };

  const pickImage = () => {
    setImagePickerAlertVisible(true);
  };

  const handelAddProduct = async () => {
    // Check if user is logged in (safe navigation)
    if (!user?.username) {
      Toast.show(i18n.t("pleaseLogin"), {
        type: "danger",
      });
      router.replace("/(routes)/(auth)/login");
      return null;
    }

    if (!sellPrice || !stock || !BoughtPrice || !name || !typeOfQuantity) {
      Toast.show(i18n.t("fillall"), {
        type: "danger",
      });
      return null;
    }

    // Validate that unit type shouldn't contain percentage values
    if (
      typeOfQuantity === "unit" &&
      (stock.toString().includes("%") || stock.toString().includes("percent"))
    ) {
      Toast.show(i18n.t("unitTypeCannotHavePercentage"), {
        type: "danger",
      });
      return null;
    }

    if (Number(BoughtPrice) < 0 || Number(sellPrice) < 0 || Number(stock) < 0) {
      Toast.show(i18n.t("validevalueerror"), { type: "danger" });
      return null;
    }
    if (Number(BoughtPrice) > Number(sellPrice)) {
      Toast.show(i18n.t("priceValidation"), {
        type: "danger",
      });
      return null;
    }

    setButtonSpinner(true);

    try {
      if (isEditMode) {
        // Edit existing product - use FormData for file upload
        const formData = new FormData();
        formData.append("name", name);
        if (barcode) formData.append("barcode", barcode.toString());
        formData.append("price_bought", BoughtPrice.toString());
        formData.append("stock", stock.toString());
        formData.append("typeOfQuantity", typeOfQuantity.toString());
        formData.append("price_sell", sellPrice.toString());

        // Handle photo upload
        if (image && image !== paramPhoto) {
          // New image selected - force JPG format
          const timestamp = Date.now();
          const filename = `photo_${timestamp}.jpg`;

          formData.append("photo", {
            uri: image,
            name: filename,
            type: "image/jpeg", // Always use JPG
          } as any);
        }

        const res = await customAxios.patch(
          `/stock/${params.productId}`,
          formData,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "multipart/form-data",
            },
            timeout: 30000, // 30 second timeout to prevent infinite loading
          }
        );

        Toast.show(i18n.t("productUpdateSuccess"), {
          type: "success",
        });
      } else {
        // Add new product - use FormData for file upload
        const formData = new FormData();

        try {
          if (barcode) {
            formData.append("barcode", barcode.toString());
          }
          formData.append("name", name);
          formData.append("price_sell", sellPrice.toString());
          formData.append("stock", stock.toString());
          formData.append("price_bought", BoughtPrice.toString());
          formData.append("typeOfQuantity", typeOfQuantity.toString());
        } catch (formError) {
          throw formError;
        }

        // Only add Userid if we have it (API might auto-detect from token)
        if ((user as any)?.id) {
          formData.append("Userid", (user as any).id.toString());
        }

        // Handle photo upload
        if (image) {
          try {
            const timestamp = Date.now();
            const filename = `product_${timestamp}.jpg`; // Always use JPG extension

            formData.append("photo", {
              uri: image,
              name: filename,
              type: "image/jpeg", // Force JPG format to avoid compatibility issues
            } as any);
          } catch (photoError) {
            Toast.show("Image processing failed. Continuing without photo.", {
              type: "warning",
            });
            // Continue without photo if there's an error
          }
        }

        const res = await customAxios.post(`/stock/add`, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000, // 30 second timeout to prevent infinite loading
        });

        if (barcode) {
          setScanning(true); // Lock scanning
        }
        Toast.show(
          isEditMode ? i18n.t("updateSuccess") : i18n.t("productAddedSuccess"),
          {
            type: "success",
          }
        );
      }

      setButtonSpinner(false);
      router.back();
    } catch (error: any) {
      setButtonSpinner(false);

      // Check for timeout error
      if (
        error?.code === "ECONNABORTED" ||
        error?.message?.includes("timeout")
      ) {
        Toast.show(
          "Request timed out. Please check your internet connection and try again.",
          {
            type: "danger",
            duration: 5000,
          }
        );
        return;
      }

      // Check if this is a free tier limit exceeded error (403)
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.error?.includes("Free tier limit exceeded")
      ) {
        // Show upgrade message and navigate to limits page
        Toast.show(
          i18n.t("freeTierLimitExceeded") + " " + i18n.t("upgradeRequired"),
          {
            type: "danger",
            duration: 5000,
          }
        );

        // Navigate to limits page after a short delay
        setTimeout(() => {
          router.push("/(drawer)/params/limits");
        }, 2000);
        return;
      }

      // Better error handling for other errors
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        (isEditMode ? i18n.t("updateFailed") : i18n.t("addfaild"));

      Toast.show(errorMessage, {
        type: "danger",
      });
    }
  };

  const handelScanProduct = async (code: string) => {
    if (scanning) return;
    setScanning(true);
    setBarcode(code);
    playSound();
    Toast.show(i18n.t("successScan"), {
      type: "success",
    });

    setOpenScan(false);
    setTimeout(() => {
      setScanning(false);
    }, 1000);
  };

  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebAddProductScreen />;
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: "#f5f5f5" }}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
    >
      {/* Standard Navigation */}
      <StandardNavigation
        title={isEditMode ? i18n.t("editproduct") : i18n.t("addproduct")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
        icon={<AddProductHeaderIcon width={30} height={30} />}
      />

      <ScrollView
        className="flex-1"
        style={{ backgroundColor: "#f5f5f5" }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <CameraScanner
          OpenCam={OpenScan}
          SetOpenCam={setOpenScan}
          handelScanProduct={handelScanProduct}
          scanning={scanning}
          locale={locale}
          showText={false}
        />

        <View className="w-full px-4 py-4">
          {/* Product Image Section */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(100)}
            style={{
              backgroundColor: "white",
              borderRadius: 20,
              padding: 20,
              marginBottom: 20,
              alignItems: "center",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            {image ? (
              <Image
                source={{ uri: image as string }}
                style={{
                  width: 150,
                  height: 150,
                  borderRadius: 15,
                  marginBottom: 15,
                }}
                resizeMode="cover"
              />
            ) : (
              <View
                style={{
                  width: 150,
                  height: 150,
                  borderRadius: 15,
                  backgroundColor: "#f0f0f0",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: 15,
                  borderWidth: 2,
                  borderStyle: "dashed",
                  borderColor: "#ccc",
                }}
              >
                <MaterialCommunityIcons
                  name="image-outline"
                  size={60}
                  color="#ccc"
                />
              </View>
            )}

            <Text
              style={{
                fontSize: 16,
                color: image ? "#279F9E" : "#666",
                fontWeight: "600",
                marginBottom: 15,
                textAlign: "center",
              }}
            >
              {image ? i18n.t("imageSelected") : i18n.t("noImageSelected")}
            </Text>

            {/* Action Buttons */}
            <View
              style={{
                flexDirection: locale === "ar" ? "row-reverse" : "row",
                width: "100%",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              {/* Add Picture Button */}
              <TouchableOpacity
                onPress={pickImage}
                style={{
                  flex: 1,
                  backgroundColor: "#279F9E",
                  flexDirection: locale === "ar" ? "row-reverse" : "row",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 12,
                  paddingVertical: 12,
                  paddingHorizontal: 15,
                  gap: 8,
                }}
              >
                <MaterialCommunityIcons name="image" size={22} color="white" />
                <Text
                  style={{ fontSize: 15, color: "white", fontWeight: "600" }}
                >
                  {i18n.t("addpicture")}
                </Text>
              </TouchableOpacity>

              {/* Scan Barcode Button */}
              <TouchableOpacity
                onPress={() => setOpenScan(true)}
                style={{
                  flex: 1,
                  backgroundColor: "#1a1a1a",
                  flexDirection: locale === "ar" ? "row-reverse" : "row",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 12,
                  paddingVertical: 12,
                  paddingHorizontal: 15,
                  gap: 8,
                }}
              >
                <MaterialCommunityIcons
                  name="barcode-scan"
                  size={22}
                  color="#4ade80"
                />
                <Text
                  style={{ fontSize: 15, color: "white", fontWeight: "600" }}
                >
                  {i18n.t("scanProduct")}
                </Text>
              </TouchableOpacity>
            </View>
          </Animated.View>

          {/* Product Information Section */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(200)}
            style={{
              backgroundColor: "white",
              borderRadius: 20,
              padding: 20,
              marginBottom: 20,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            <Text
              style={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#279F9E",
                marginBottom: 20,
                textAlign: locale === "ar" ? "right" : "left",
              }}
            >
              {i18n.t("productName")}
            </Text>

            <FloatingLabelInput
              label={i18n.t("productName")}
              value={name}
              onChangeText={setName}
              locale={locale}
            />

            {/* Barcode Field */}
            <FloatingLabelInput
              label={i18n.t("barcode")}
              value={barcode}
              onChangeText={setBarcode}
              locale={locale}
              keyboardType="numeric"
            />
          </Animated.View>

          {/* Stock Information Section */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(300)}
            style={{
              backgroundColor: "white",
              borderRadius: 20,
              padding: 20,
              marginBottom: 20,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            <Text
              style={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#279F9E",
                marginBottom: 20,
                textAlign: locale === "ar" ? "right" : "left",
              }}
            >
              {i18n.t("quantity")}
            </Text>

            {/* Quantity field and its type */}
            <View
              style={{
                flexDirection: locale === "ar" ? "row-reverse" : "row",
                alignItems: "center",
                gap: 12,
                marginBottom: 10,
              }}
            >
              <View style={{ flex: 0.6 }}>
                <FloatingLabelInput
                  label={i18n.t("quantity")}
                  value={stock}
                  onChangeText={setStock}
                  locale={locale}
                  keyboardType="numeric"
                  style={{
                    margin: 0,
                    marginBottom: 0,
                  }}
                />
              </View>

              <View
                style={{
                  flex: 0.4,
                  backgroundColor: "white",
                  borderWidth: 1.5,
                  borderRadius: 15,
                  borderColor: "#279F9E",
                  height: 55,
                  justifyContent: "center",
                  shadowColor: "#000",
                  shadowOffset: {
                    width: 0,
                    height: 1,
                  },
                  shadowOpacity: 0.1,
                  shadowRadius: 2,
                  elevation: 2,
                }}
              >
                {/* Label for picker */}
                <Text
                  style={{
                    position: "absolute",
                    top: -8,
                    left: locale === "ar" ? undefined : 12,
                    right: locale === "ar" ? 12 : undefined,
                    backgroundColor: "white",
                    paddingHorizontal: 8,
                    fontSize: 12,
                    color: "#279F9E",
                    fontWeight: "600",
                    zIndex: 1,
                  }}
                >
                  {i18n.t("typeOfQuantity")}
                </Text>

                <Picker
                  selectedValue={typeOfQuantity}
                  onValueChange={(itemValue) => setTypeOfQuantity(itemValue)}
                  style={{
                    height: 55,
                    color: "black",
                    fontSize: 16,
                    fontWeight: "600",
                  }}
                  itemStyle={{
                    textAlign: locale === "ar" ? "right" : "left",
                    fontSize: 16,
                    fontWeight: "600",
                  }}
                >
                  {quantitiesTypes.map((item) => (
                    <Picker.Item
                      key={item.value}
                      label={item.label}
                      value={item.value}
                      style={{
                        textAlign: locale === "ar" ? "right" : "left",
                        direction: locale === "ar" ? "rtl" : "ltr",
                      }}
                    />
                  ))}
                </Picker>
              </View>
            </View>
          </Animated.View>

          {/* Pricing Information Section */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(400)}
            style={{
              backgroundColor: "white",
              borderRadius: 20,
              padding: 20,
              marginBottom: 20,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            <Text
              style={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#279F9E",
                marginBottom: 20,
                textAlign: locale === "ar" ? "right" : "left",
              }}
            >
              {i18n.t("sellPrice")} & {i18n.t("buyPrice")}
            </Text>

            <View
              style={{
                flexDirection: locale === "ar" ? "row-reverse" : "row",
                gap: 12,
                marginBottom: 10,
              }}
            >
              <View style={{ flex: 1 }}>
                <FloatingLabelInput
                  label={i18n.t("sellPrice")}
                  value={sellPrice}
                  onChangeText={setSellPrice}
                  locale={locale}
                  keyboardType="numeric"
                />
              </View>
              <View style={{ flex: 1 }}>
                <FloatingLabelInput
                  label={i18n.t("buyPrice")}
                  value={BoughtPrice}
                  onChangeText={setBoughtPrice}
                  locale={locale}
                  keyboardType="numeric"
                />
              </View>
            </View>

            {/* Profit Preview */}
            {BoughtPrice &&
              sellPrice &&
              Number(sellPrice) > Number(BoughtPrice) && (
                <View
                  style={{
                    backgroundColor: "#f0fdf4",
                    borderRadius: 12,
                    padding: 12,
                    marginTop: 10,
                    borderWidth: 1,
                    borderColor: "#86efac",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      color: "#16a34a",
                      fontWeight: "600",
                      textAlign: locale === "ar" ? "right" : "left",
                    }}
                  >
                    {i18n.t("profit") || "Profit"}:{" "}
                    {(Number(sellPrice) - Number(BoughtPrice)).toFixed(2)} DZD
                  </Text>
                </View>
              )}
          </Animated.View>

          {/* Delete Button (Edit Mode Only) */}
          {isEditMode && (
            <Animated.View entering={FadeInUp.duration(600).delay(450)}>
              <TouchableOpacity
                style={{
                  borderRadius: 15,
                  overflow: "hidden",
                  marginBottom: 12,
                  backgroundColor: "#FEE2E2",
                  borderWidth: 1.5,
                  borderColor: "#FCA5A5",
                  paddingVertical: 16,
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onPress={() => setShowDeleteModal(true)}
              >
                <View
                  style={{
                    flexDirection: locale === "ar" ? "row-reverse" : "row",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <MaterialCommunityIcons
                    name="delete-outline"
                    size={24}
                    color="#DC2626"
                  />
                  <Text
                    style={{
                      fontSize: 18,
                      fontWeight: "bold",
                      color: "#DC2626",
                    }}
                  >
                    {i18n.t("deleteProduct")}
                  </Text>
                </View>
              </TouchableOpacity>
            </Animated.View>
          )}

          {/* Submit Button */}
          <Animated.View entering={FadeInUp.duration(600).delay(500)}>
            <TouchableOpacity
              style={{
                borderRadius: 15,
                overflow: "hidden",
                marginBottom: 30,
              }}
              disabled={buttonSpinner}
              onPress={handelAddProduct}
            >
              <LinearGradient
                colors={["rgba(19, 121, 162, 1)", "rgba(5, 150, 208, 1)"]}
                style={{
                  paddingVertical: 16,
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 15,
                }}
              >
                {buttonSpinner ? (
                  <ActivityIndicator size="small" color={"white"} />
                ) : (
                  <View
                    style={{
                      flexDirection: locale === "ar" ? "row-reverse" : "row",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <MaterialCommunityIcons
                      name={isEditMode ? "content-save" : "check-circle"}
                      size={24}
                      color="white"
                    />
                    <Text
                      style={{
                        fontSize: 18,
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      {isEditMode ? i18n.t("save") : i18n.t("confirm")}
                    </Text>
                  </View>
                )}
              </LinearGradient>
            </TouchableOpacity>
          </Animated.View>
        </View>
      </ScrollView>

      {/* Custom Delete Confirmation Modal */}
      <Modal
        visible={showDeleteModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowDeleteModal(false)}
      >
        <Pressable
          onPress={() => setShowDeleteModal(false)}
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.5)",
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
          }}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              borderRadius: 20,
              padding: 28,
              width: "100%",
              maxWidth: 340,
              alignItems: "center",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.25,
              shadowRadius: 24,
              elevation: 20,
            }}
          >
            {/* Warning Icon */}
            <View
              style={{
                width: 64,
                height: 64,
                borderRadius: 32,
                backgroundColor: "#FEE2E2",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <MaterialCommunityIcons
                name="delete-alert"
                size={36}
                color="#DC2626"
              />
            </View>

            <Text
              style={{
                fontSize: 20,
                fontWeight: "700",
                color: "#DC2626",
                marginBottom: 8,
                textAlign: "center",
              }}
            >
              {i18n.t("deleteProduct")}
            </Text>
            <Text
              style={{
                fontSize: 15,
                color: "#666",
                textAlign: "center",
                marginBottom: 8,
                lineHeight: 22,
              }}
            >
              {i18n.t("confirmDelete")}
            </Text>
            <Text
              style={{
                fontSize: 17,
                fontWeight: "600",
                color: "#333",
                marginBottom: 24,
                textAlign: "center",
              }}
            >
              {name || paramProductName}
            </Text>

            <View
              style={{
                flexDirection: "row",
                gap: 12,
                width: "100%",
              }}
            >
              <TouchableOpacity
                onPress={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: "#F3F4F6",
                  borderWidth: 1,
                  borderColor: "#E5E7EB",
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  {i18n.t("cancel")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={async () => {
                  setIsDeleting(true);
                  const success = await deleteProduct(Number(params.productId));
                  setIsDeleting(false);
                  setShowDeleteModal(false);
                  if (success) {
                    router.back();
                  }
                }}
                disabled={isDeleting}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: "#DC2626",
                  alignItems: "center",
                  opacity: isDeleting ? 0.6 : 1,
                }}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: "600",
                      color: "#FFFFFF",
                    }}
                  >
                    {i18n.t("delete")}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <CustomAlert
        visible={isImagePickerAlertVisible}
        title={i18n.t("addpicture")}
        message={i18n.t("chooseOption")}
        icon="camera"
        onDismiss={() => setImagePickerAlertVisible(false)}
        buttons={[
          {
            text: i18n.t("takePhoto"),
            onPress: async () => {
              setImagePickerAlertVisible(false);
              const cameraPermission =
                await ImagePicker.requestCameraPermissionsAsync();
              if (!cameraPermission.granted) return;

              let result = await ImagePicker.launchCameraAsync({
                allowsEditing: false, 
                quality: 0.8, 
                exif: false, 
                base64: false, 
              });

              if (!result.canceled) {
                const processedUri = await processImageWithCompression(
                  result.assets[0].uri,
                  true
                );
                if (processedUri) {
                  setImage(processedUri);
                }
              }
            },
          },
          {
            text: i18n.t("chooseFromGallery"),
            onPress: async () => {
              setImagePickerAlertVisible(false);
              const mediaPermission =
                await ImagePicker.requestMediaLibraryPermissionsAsync();
              if (!mediaPermission.granted) return;

              let result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ["images"], 
                allowsEditing: false, 
                quality: 0.8, 
                exif: false, 
                base64: false, 
              });

              if (!result.canceled) {
                const processedUri = await processImageWithCompression(
                  result.assets[0].uri,
                  false
                );
                if (processedUri) {
                  setImage(processedUri);
                }
              }
            },
          },
          {
            text: i18n.t("cancel"),
            style: "cancel",
            onPress: () => setImagePickerAlertVisible(false),
          },
        ]}
      />
    </KeyboardAvoidingView>
  );
};

export default AddProduct;
